/**
 * THE CLIENT'S PROGRESS PHOTOS ON THE COACH'S OVERVIEW — `EVERFIT-MAP` §2-bis column 2's
 * `Progress Photo` card (Fable's ruling 2026-09-24, 3(a)). Theirs, empty: *"Yiannis hasn't
 * uploaded any photos."* — copied verbatim in shape.
 *
 * ── WHAT IT READS, AND WHY NOTHING HAD TO CHANGE ─────────────────────────────────────────────
 *
 * The photos are the athlete's own weigh-in uploads: `body_measurements.photo_{front,side,back}_path`
 * (migration `20260914110000`), objects in the PRIVATE `progress-photos` bucket. Both reads a
 * coach needs are already live policy: `body_measurements_coach_read` (`20260914130000`) for the
 * row and `progress_photos_active_coach_read` (`20260914120000`) for the object — each admitting
 * the client's ACTIVE, ACCEPTED coach only. **No schema, no policy, no grant is added here.**
 *
 * ⚠️ **AN AUTHENTICATED DOWNLOAD, NEVER A SIGNED URL.** Fable's ruling allowed short-lived signed
 * URLs; a download is stricter still — the image is fetched with the coach's own token under the
 * same SELECT policy and lives in the page as a blob, so there is no URL at all that could be
 * copied into a message and opened by somebody else for its lifetime.
 *
 * ⚠️ MEASURED 2026-09-24: 0 of the 3 live `body_measurements` rows carry a photo, so every coach
 * reads the empty sentence today. The PHOTO state has been rendered on the dev stub only
 * (`?ht-photos`), with placeholder images — a successful live read is UNMEASURED until an athlete
 * uploads one.
 */

export const PROGRESS_PHOTO_BUCKET = "progress-photos";

export type PhotoView = "front" | "side" | "back";
export const PHOTO_VIEWS: readonly PhotoView[] = ["front", "side", "back"];

export type MeasurementPhotoRow = {
  measured_at: string;
  photo_front_path: string | null;
  photo_side_path: string | null;
  photo_back_path: string | null;
};

export type PhotoSet = {
  /** `body_measurements.measured_at` — a timestamp. */
  measuredAtISO: string;
  /** Only the views the athlete actually took, in front · side · back order. */
  photos: { view: PhotoView; path: string }[];
};

const pathOf = (row: MeasurementPhotoRow, view: PhotoView): string | null => {
  const raw = view === "front" ? row.photo_front_path : view === "side" ? row.photo_side_path : row.photo_back_path;
  return raw === null || raw.trim() === "" ? null : raw;
};

/**
 * The newest weigh-in that carries at least one photo, or null. Sorted HERE rather than trusted
 * from the query's order, because a reader that relies on an ORDER BY it did not write is one
 * refactor away from showing last spring's photos as the latest.
 */
export function latestPhotoSet(rows: readonly MeasurementPhotoRow[]): PhotoSet | null {
  const withPhotos = rows
    .map((r) => ({ r, photos: PHOTO_VIEWS.flatMap((view) => {
      const path = pathOf(r, view);
      return path === null ? [] : [{ view, path }];
    }) }))
    .filter((x) => x.photos.length > 0)
    .sort((a, b) => (a.r.measured_at < b.r.measured_at ? 1 : a.r.measured_at > b.r.measured_at ? -1 : 0));
  const first = withPhotos[0];
  return first === undefined ? null : { measuredAtISO: first.r.measured_at, photos: first.photos };
}

/** Their empty sentence, with the client's first name — `Yiannis hasn't uploaded any photos.` */
export function noPhotosSentence(firstName: string | null): string {
  const who = firstName === null || firstName.trim() === "" ? "Your client" : firstName.trim();
  return `${who} hasn't uploaded any photos.`;
}

/** The view's word, for the caption under each photo and its alt text. */
export function viewLabel(view: PhotoView): string {
  return view === "front" ? "Front" : view === "side" ? "Side" : "Back";
}
